import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AdminService } from 'src/app/admin-pages/admin.service';

@Component({
  selector: 'app-payment-methods',
  templateUrl: './payment-methods.component.html',
  styleUrls: ['./payment-methods.component.scss'],
})
export class PaymentMethodsComponent {
  @ViewChild('paymentsContainer') paymentsContainer: ElementRef<HTMLDivElement>;
  @ViewChildren('amountInput') amountInputs!: QueryList<ElementRef<HTMLInputElement>>;

  @Input() totalReceivable: any;
  @Input() calledFrom: string = '';
  @Input() prevPayInfo: any = null;
  @Input() advanceEditAmount: any;
  @Input() refundAmount: any;
  @Input() isPaidJob: boolean;
  @Output() paymentSaved = new EventEmitter<{ paymentModes: any[]; totalAmount: number; changeToReturn: number; calledFrom: string; totalCashReceived: number }>();
  @Output() close = new EventEmitter<void>();
  paymentForm!: FormGroup;
  paymentMethods: any[] = [];
  paymentMethodsData: any[] = [];
  confirmButtonTitle: string = 'Confirm Payment';
  disableAddMore: boolean;
  disableMethodSelect: boolean;
  disableMethodDelete: boolean;
  transactionMode: 'add' | 'update' = 'add';
  amountDescription: string = 'Receivable Amount';
  showPaymentMethods = false;
  previousValues: { [index: number]: any } = {};
  loading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private adminService: AdminService,
  ) { }

  ngOnInit(): void {
    if (this.calledFrom == 'edit-advance' || this.calledFrom == 'add-advance') {
      this.totalReceivable = this.advanceEditAmount;
    } else if (this.calledFrom == 'refund' || this.calledFrom === 'return') {
      this.totalReceivable = this.refundAmount;
      this.amountDescription = 'Refund Amount';
    }
    this.getPaymentModes();
    this.paymentForm = this.fb.group({
      payments: this.fb.array([this.createPaymentGroup(this.totalReceivable)])
    });

    if (this.calledFrom == 'advance' || this.calledFrom == 'edit-advance' || this.calledFrom == 'add-advance') {
      this.confirmButtonTitle = 'Save';
      this.amountDescription = 'Advance Amount';
    }
    if (this.calledFrom == 'update' || this.calledFrom == 'edit-advance') {
      this.disableAddMore = true;
      this.disableMethodSelect = true;
      this.disableMethodDelete = true;
      this.transactionMode = 'update';
    }

  }

  get payments(): FormArray {
    return this.paymentForm.get('payments') as FormArray;
  }

  get totalAmount(): number {
    return this.payments.controls.reduce((sum, group) => {
      const amount = group.get('amount')?.value || 0;
      return sum + Number(amount);
    }, 0);
  }

  get totalCashReceived(): number {
    return this.payments.controls.reduce((sum, group) => {
      const method = group.get('method')?.value;
      const amount = Number(group.get('amount')?.value) || 0;
      if (method?.Method === 'Cash') {
        sum += amount;
      }
      return sum;
    }, 0);
  }

  get totalReceivedAmount() {
    return this.payments.controls.reduce((sum, group) => {
      const method = group.get('method')?.value;
      const amount = Number(group.get('amount')?.value) || 0;
      if (method?.Method !== 'Credit') {
        sum += amount;
      }
      return sum;
    }, 0);
  }

  get totalReceivedExcludingCash(): number {
    return this.payments.controls.reduce((sum, group) => {
      const method = group.get('method')?.value;
      const amount = Number(group.get('amount')?.value) || 0;
      if (method?.Method !== 'Cash') {
        sum += amount;
      }
      return sum;
    }, 0);
  }

  public getChangeToReturn(): number {
    const due = this.totalReceivable;
    const receivedCash = this.totalCashReceived || 0;
    if (this.totalReceivedExcludingCash >= due) {
      return 0;
    } else {
      const actualCashToBeReceived = due - this.totalReceivedExcludingCash;
      return receivedCash > actualCashToBeReceived ? receivedCash - actualCashToBeReceived : 0;
    }
  }

  get isConfirmPaymentDisabled(): boolean {
    const allPaymentsValid = this.payments.controls.every(group => group.valid);

    const totalMatchesReceivable =
      (this.totalAmount - this.getChangeToReturn()) === this.totalReceivable &&
      this.totalReceivable > 0;

    return !allPaymentsValid || !totalMatchesReceivable;
  }

  get isAllPaymentsValid() {
    const allPaymentsValid = this.payments.controls.every(group => group.valid);
    return allPaymentsValid;
  }

  get totalMatchedReceivable() {
    const totalMatchesReceivable =
      (this.totalAmount - this.getChangeToReturn()) === this.totalReceivable &&
      this.totalReceivable > 0;
    return !totalMatchesReceivable;
  }

  onClose() {
    this.close.emit();
  }

  createPaymentGroup(balance: null | number = null): FormGroup {
    return this.fb.group({
      amount: [balance, [Validators.required, Validators.min(1)]],
      method: [null, Validators.required],
      isSelected: [false],
      firstTimeSelection: [true],
    });
  }

  addPayment(): void {
    const balance = this.totalReceivable - this.totalAmount;
    this.payments.controls.forEach((ctrl, i) => {
      ctrl.patchValue({ isSelected: true });
    });
    this.payments.push(this.createPaymentGroup(balance));

  }

  removePayment(index: number): void {
    this.payments.removeAt(index);
  }

  onSubmit() {
    const paymentValues = this.payments.value;
    const LedgerConnectedKeyArray = paymentValues.map(p => ({
      "Key": p.method.LedgerConnectedKey,
      "Value": p.method.Method === 'Cash'
        ? p.amount - this.getChangeToReturn()
        : p.amount,
      "TransactionMode": this.transactionMode,
      "Id": p.method.Id,
    }));
    const LedgerConnectedKeyForCustomerArray = paymentValues.map(p => ({
      "Key": p.method.LedgerConnectedKeyForCustomer,
      "Value": p.method.Method === 'Cash'
        ? p.amount - this.getChangeToReturn()
        : p.amount,
      "TransactionMode": this.transactionMode,
      "Id": p.method.Id,
    }));
    const paymentModesArray = [...LedgerConnectedKeyArray, ...LedgerConnectedKeyForCustomerArray];
    this.paymentSaved.emit({
      paymentModes: paymentModesArray,
      totalAmount: this.totalReceivedAmount,
      changeToReturn: this.getChangeToReturn(),
      calledFrom: this.calledFrom,
      totalCashReceived: this.totalCashReceived,
    });
    this.onClose();
  }

  public getPaymentModes() {
    this.loading = true;
    this.adminService.getPaymentModesBySeller().subscribe(
      (res: any) => {

        if (res.Result === 'Success' && res?.ResponseData) {
          this.paymentMethodsData = res?.ResponseData;
          if (this.calledFrom === 'pay-in' || this.calledFrom === 'advance' || this.calledFrom == 'add-advance' || this.calledFrom === 'edit-advance' || this.calledFrom === 'refund' || this.calledFrom === 'return') {
            this.paymentMethodsData = this.paymentMethodsData.filter(item => item.Method !== 'Credit');
          }
          this.paymentMethods = this.paymentMethodsData.map(item => ({
            ...item,
            label:
              item.Method === 'UPI' || item.Method === 'Card'
                ? `${item.Method} (${item.BankCode} - ${item.AccountNumber})`
                : `${item.Method}`
          }));
          if (this.calledFrom == 'update' || this.calledFrom == 'edit-advance') {
            this.patchPaymentsToFormArray(JSON.parse(this.prevPayInfo));
          }
        }
        this.loading = false;
      },
      (error: HttpErrorResponse) => {
        this.loading = false;
      }
    );
  }

  private patchPaymentsToFormArray(paymentData: any[]) {
    const paymentsArray = this.paymentForm.get('payments') as FormArray;
    paymentsArray.clear();

    paymentData.forEach(payment => {

      const methodObj = this.paymentMethods.find(
        m => m.Id == payment.PaymentModeId
      );

      paymentsArray.push(this.fb.group({
        amount: [payment.Amount],
        method: [methodObj || null],
        isSelected: [true],
        firstTimeSelection: [false],
      }));
    });
  }

  private scrollToBottom() {
    if (this.paymentsContainer && this.paymentsContainer.nativeElement) {
      const el = this.paymentsContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  selectPaymentMethod(method: any, index: number) {
    const group = this.payments.at(index);
    group.patchValue({ method: method, isSelected: true, firstTimeSelection: false });
  }

  editPaymentMethod(index: number) {
    if(!this.isAllPaymentsValid){
      return;
    }
    const group = this.payments.at(index);
    this.previousValues[index] = {
      method: group.get('method')?.value,
      amount: group.get('amount')?.value
    };
    this.payments.controls.forEach((ctrl, i) => {
      if (i !== index) {
        ctrl.patchValue({ isSelected: true });
      }
    });
    group.patchValue({ isSelected: false, firstTimeSelection: false });

  }

  clearPaymentMethod(index: number) {
    const group = this.payments.at(index);
    group.patchValue({ method: null, amount: null });
  }

  cancelPaymentMethod(index: number, prevValue: any) {
    const group = this.payments.at(index);
    const prev = this.previousValues[index];
    if (prev) {
      group.patchValue({
        method: prev.method,
        amount: prev.amount,
        isSelected: true
      });
    }
  }

  isMethodSelected(method: any, index: number): boolean {
    const group = this.payments.at(index);
    return group.get('method')?.value === method;
  }

  public getMethodIcon(method: string): string {
    switch (method) {
      case 'Cash': return 'bi bi-cash-stack';
      case 'Credit': return 'bi bi-credit-card';
      case 'Card': return 'bi bi-credit-card-2-front';
      case 'UPI': return 'bi bi-qr-code-scan';
      default: return 'bi bi-wallet2';
    }
  }

  public getMethodColor(method: string): string {
    switch (method) {
      case 'Cash':
        return '#9E9D24';
      case 'Card':
        return '#90A4AE';
      case 'UPI':
        return '#C2185B';
      case 'Credit':
        return '#81D4FA';
      default:
        return '#6c757d';
    }
  }

}
